// The Klaviyo handover (0028), beside the pools: per country, how much of it
// Klaviyo still mails. While a country is migrating, its Klaviyo campaigns
// exclude `emc_wave <= N` and we mail only the contacts Klaviyo was told about
// with a wave of N or lower; once it is cut over, Klaviyo mails nobody there
// and nothing restricts us. Each move asks why, goes through the route that
// calls the CLI's function, and is admin-only — the API refuses anybody else.
// Each row opens its country's waves below the table (`WavesPanel`): who is in
// which wave, the automatic emc_wave write, and a wave from a Klaviyo segment.
// A country can also be switched to parallel (0045, owner 2026-10-23): Klaviyo
// keeps mailing everybody and the handover filters nobody; every other
// exclusion still applies. Switching it off brings the wave line back.
// Since 2026-10-23 the panel sits in the board's "Részletek"; the rows are the
// board's (the country list on top reads the same ones), and every move here
// reloads the board too, since each one redoes the account's split at once.

import { useId, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeftIcon, LayersIcon, PencilIcon, SplitIcon, Undo2Icon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { cutOverHandover, reopenHandover, setHandoverWave } from "@/lib/api";
import type { Handover } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable } from "@/components/emc/DataTable";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { useIsAdmin } from "@/pages/warmup/actions";
import { PARALLEL, meaningOf, waveOf } from "@/pages/warmup/handover";
import { Country } from "@/components/emc/Flag";
import { ParallelDialog } from "@/pages/warmup/ParallelDialog";
import { WavesPanel } from "@/pages/warmup/WavesPanel";

// The API's note limit (`HandoverNoteIn`).
const NOTE_MAX = 500;

type Move = { kind: "wave" | "cut_over" | "reopen" | "parallel_on" | "parallel_off"; row: Handover };

interface Props {
  rows: Handover[] | null;
  error: string | null;
  /** Reads the rows and the board again: a move here moves the split too. */
  reload: () => void;
}

export function HandoverPanel({ rows, error, reload }: Props) {
  const isAdmin = useIsAdmin();
  const [move, setMove] = useState<Move | null>(null);
  const [wavesOf, setWavesOf] = useState<string | null>(null);
  const [wave, setWave] = useState("");
  const waveId = useId();

  function open(kind: Move["kind"], row: Handover) {
    setWave(kind === "reopen" ? String(row.wave) : "");
    setMove({ kind, row });
  }

  function done(message: string) {
    toast(message);
    setMove(null);
    reload();
  }

  const closed = (rows ?? []).filter((row) => row.restricts && row.wave === 0).map((row) => row.country);
  const parallel = (rows ?? []).filter((row) => row.parallel === true).map((row) => row.country);

  const columns: ColumnDef<Handover>[] = [
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => <Country code={row.original.country} />,
    },
    {
      id: "state",
      header: "Állapot",
      cell: ({ row }) => <StatusPill domain="handover" state={row.original.state} />,
    },
    {
      id: "wave",
      header: "N",
      cell: ({ row }) =>
        row.original.restricts ? (
          <span className="data-mono font-semibold">{row.original.wave}</span>
        ) : row.original.parallel ? (
          <span className="text-muted-foreground">
            nincs szűrés (megtartott N = <span className="data-mono">{row.original.wave}</span>)
          </span>
        ) : (
          <span className="text-muted-foreground">nincs korlát</span>
        ),
      meta: { align: "right" },
    },
    {
      id: "meaning",
      header: "Kinek küldünk",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {meaningOf(row.original)}
        </span>
      ),
      meta: { wrap: true },
    },
    {
      id: "waves",
      header: "Hullámok",
      cell: ({ row }) => (
        <span className="flex flex-col items-start gap-1">
          <Button
            size="sm"
            variant={wavesOf === row.original.country ? "secondary" : "outline"}
            type="button"
            onClick={() => setWavesOf(wavesOf === row.original.country ? null : row.original.country)}
          >
            <LayersIcon />
            Hullámok
          </Button>
          <span className="text-label text-muted-foreground">
            emc_wave írás: {row.original.wave_sync ? "be" : "ki"}
          </span>
          {row.original.wave_job && <StatusPill domain="waveJob" state={row.original.wave_job} />}
        </span>
      ),
    },
    {
      id: "changed",
      header: "Utoljára",
      cell: ({ row }) => (
        <span className="flex flex-col gap-0.5">
          <span>
            {row.original.changed_by} ·{" "}
            <span className="data-mono">{minute(row.original.changed_at)}</span>
          </span>
          <span className="text-label text-muted-foreground">{row.original.note}</span>
        </span>
      ),
      meta: { wrap: true },
    },
  ];
  if (isAdmin) {
    columns.push({
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.restricts || row.original.parallel ? (
          <span className="flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" type="button" onClick={() => open("wave", row.original)}>
              <PencilIcon />
              N módosítása
            </Button>
            {row.original.parallel ? (
              <Button size="sm" variant="outline" type="button" onClick={() => open("parallel_off", row.original)}>
                <Undo2Icon />
                Átadás-szűrés vissza
              </Button>
            ) : (
              <Button size="sm" variant="outline" type="button" onClick={() => open("parallel_on", row.original)}>
                <SplitIcon />
                A Klaviyo is küld
              </Button>
            )}
            {/* A final step: once cut over, nothing holds the country back. Not the
                row's loudest button; the dialog asks for a note as before. */}
            <Button size="sm" variant="outline-danger" type="button" onClick={() => open("cut_over", row.original)}>
              <ArrowRightLeftIcon />
              Átállás
            </Button>
          </span>
        ) : (
          <span className="flex justify-end">
            <Button size="sm" variant="outline" type="button" onClick={() => open("reopen", row.original)}>
              <Undo2Icon />
              Visszavonás
            </Button>
          </span>
        ),
      meta: { align: "right" },
    });
  }

  const row = move?.row;
  const waveField = (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={waveId}>Új N (0–7)</Label>
      <Input
        id={waveId}
        inputMode="numeric"
        value={wave}
        placeholder={row ? String(row.wave) : ""}
        onChange={(event) => setWave(event.target.value)}
      />
    </div>
  );

  return (
    <Card id="handover" className="scroll-mt-6">
      <CardHeader>
        <CardTitle>Klaviyo-átadás</CardTitle>
        <CardDescription>
          Amíg egy ország Klaviyo-fiókja küld, a Klaviyo-kampányai kizárják az{" "}
          <span className="data-mono">emc_wave ≤ N</span> profilokat, mi pedig csak azoknak küldünk,
          akiknek a hullámát a Klaviyo már megkapta, és legfeljebb N. Így senki sem kap levelet
          mindkettőtől. Átállás után a Klaviyo már nem küld, és nincs korlát. Párhuzamos módban
          („a Klaviyo is küld”) a Klaviyo mindenkinek küld, és mi is: nincs átadás-szűrés, minden más
          kizárás és a napi keret él.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error !== null && <ErrorState message={error} onRetry={reload} />}
        {closed.length > 0 && (
          <Callout tone="warn">
            N = 0: <span className="data-mono">{closed.join(" ")}</span> — ezekben az országokban innen
            senkinek nem megy levél, amíg valaki nem emeli N-et.
          </Callout>
        )}
        {parallel.length > 0 && (
          <Callout tone="warn">
            Párhuzamos: <span className="data-mono">{parallel.join(" ")}</span> — {PARALLEL}. Ezekben az
            országokban ugyanaz a kontakt a Klaviyótól és tőlünk is kaphat levelet.
          </Callout>
        )}
        <DataTable
          label="Klaviyo-átadás országonként"
          variant="plain"
          className="-mx-5.5 border-t border-separator"
          columns={columns}
          data={rows ?? []}
          loading={rows === null && error === null}
          getRowId={(item) => item.country}
          empty={
            <p className="px-4 py-6 text-center text-muted-foreground">
              Nincs átadás-sor: egyik országot sem korlátozza semmi.
            </p>
          }
        />
        {wavesOf !== null && (
          <WavesPanel
            country={wavesOf}
            isAdmin={isAdmin}
            onClose={() => setWavesOf(null)}
            onChanged={reload}
          />
        )}
      </CardContent>

      {row && (
        <>
          <ActionDialog
            open={move?.kind === "wave"}
            onOpenChange={(next) => !next && setMove(null)}
            title={`${row.country}: N módosítása`}
            description={
              <>
                Most N = {row.wave}. A sorrend, hogy egy pillanatra se küldjön mindkettő:{" "}
                <strong>emelésnél</strong> előbb a Klaviyóban zárd ki az{" "}
                <span className="data-mono">emc_wave ≤ új N</span> profilokat, aztán itt;{" "}
                <strong>csökkentésnél</strong> előbb itt, aztán a Klaviyóban. Emelés után az új
                hullám szinkronizált kontaktjai a mieink; csökkentés után a futó kampányok is
                kihagyják, akik újra a Klaviyóé.
              </>
            }
            confirmLabel="N módosítása"
            reason={{
              label: "Melyik Klaviyo-módosításhoz tartozik?",
              placeholder: "pl. Klaviyo HU: emc_wave <= 2 kizárva a heti kampányokból",
              max: NOTE_MAX,
            }}
            onConfirm={async (note) => {
              const next = waveOf(wave, row.wave);
              await setHandoverWave(row.country, next, note);
              done(`${row.country}: N = ${next}.`);
            }}
          >
            {waveField}
          </ActionDialog>

          <ParallelDialog
            row={row}
            enabled={move?.kind === "parallel_on"}
            open={move?.kind === "parallel_on" || move?.kind === "parallel_off"}
            onOpenChange={(next) => !next && setMove(null)}
            onDone={done}
          />

          <ActionDialog
            open={move?.kind === "cut_over"}
            onOpenChange={(next) => !next && setMove(null)}
            title={`${row.country}: átállás (a Klaviyo már nem küld)`}
            description={
              <>
                Csak akkor, ha a(z) {row.country} Klaviyo-fiókja már senkinek nem küld — kampány és flow
                sem. Innen minden feliratkozott a mienk, hullámtól függetlenül. Visszavonni kézzel,
                indoklással lehet.
              </>
            }
            confirmLabel="Átállás"
            tone="danger"
            reason={{
              label: "Mi igazolja, hogy a Klaviyo már nem küld?",
              placeholder: "pl. a HU Klaviyo-fiók kampányai és flow-i leállítva",
              max: NOTE_MAX,
            }}
            onConfirm={async (note) => {
              await cutOverHandover(row.country, note);
              done(`${row.country}: átállva.`);
            }}
          />

          <ActionDialog
            open={move?.kind === "reopen"}
            onOpenChange={(next) => !next && setMove(null)}
            title={`${row.country}: átállás visszavonása`}
            description={
              <>
                Az ország újra átadás alatt áll: csak a szinkronizált, legfeljebb N. hullámos
                kontaktoknak küldünk, a futó kampányok is. Előbb itt, aztán induljon újra a Klaviyo
                (az <span className="data-mono">emc_wave ≤ N</span> kizárással).
              </>
            }
            confirmLabel="Visszavonás"
            reason={{
              label: "Miért küld mégis a Klaviyo?",
              placeholder: "pl. egy kosárelhagyó flow még fut",
              max: NOTE_MAX,
            }}
            onConfirm={async (note) => {
              const next = waveOf(wave, null);
              await reopenHandover(row.country, next, note);
              done(`${row.country}: újra átadás alatt, N = ${next}.`);
            }}
          >
            {waveField}
          </ActionDialog>
        </>
      )}
    </Card>
  );
}
